import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, AlertTriangle, ShoppingCart, Send, X, PackageCheck } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useBca, useCreateBca, useEmettreBca, useAnnulerBca, useDeleteBca,
  useFournisseurs, useCreateReception, useBcaDetail,
} from '@/hooks/useBetonSprint5';
import { useBetonMatieres } from '@/hooks/useBetonCatalog';
import {
  STATUT_BCA_LABELS, STATUT_BCA_COLORS, type StatutBonCommande, type BonCommandeAchat,
} from '@/types';
import { formatFCFA, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });

export const AchatsPage = () => {
  const [statut, setStatut] = useState<StatutBonCommande | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [receptionFor, setReceptionFor] = useState<BonCommandeAchat | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useBca({ statut: statut || undefined, limit: 200 });
  const emettre = useEmettreBca();
  const annuler = useAnnulerBca();
  const remove = useDeleteBca();
  const bcas = data?.data ?? [];

  const kpis = useMemo(() => ({
    brouillon:           bcas.filter((b) => b.statut === 'brouillon').length,
    emis:                bcas.filter((b) => b.statut === 'emis').length,
    partielle:           bcas.filter((b) => b.statut === 'reception_partielle').length,
    recu_complet:        bcas.filter((b) => b.statut === 'recu_complet').length,
    montantEnCours: bcas
      .filter((b) => ['emis', 'reception_partielle'].includes(b.statut))
      .reduce((s, b) => s + Number(b.montantHtCents), 0),
  }), [bcas]);

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Achats / Bons de commande"
        subtitle="Module 11 — BCA avec workflow brouillon→émis→réception→reçu_complet · auto-déstockage matières + recalcul CMP"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouveau BCA
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Brouillons</div>
            <div className="ds-ks-value">{kpis.brouillon}</div>
            <div className="ds-ks-sub">À valider</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Émis</div>
            <div className="ds-ks-value">{kpis.emis}</div>
            <div className="ds-ks-sub">Attente livraison</div>
          </div>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-label">Réception partielle</div>
            <div className="ds-ks-value">{kpis.partielle}</div>
            <div className="ds-ks-sub">En cours réception</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Reçus complets</div>
            <div className="ds-ks-value">{kpis.recu_complet}</div>
            <div className="ds-ks-sub">Clôturés</div>
          </div>
        </div>

        <div style={{ padding: '8px 12px', marginBottom: 14, borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--bdr)', fontSize: 12 }}>
          <strong>Montant en-cours fournisseurs : {formatFCFA(BigInt(kpis.montantEnCours))}</strong> (BCA émis + en réception partielle)
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutBonCommande | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_BCA_LABELS) as StatutBonCommande[]).map((s) => (
              <option key={s} value={s}>{STATUT_BCA_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Bons de commande achat</div>
              <div className="ds-table-sub">{bcas.length} BCA</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Référence</th>
                <th>Fournisseur</th>
                <th style={{ width: 100 }}>Date cmd</th>
                <th style={{ width: 100 }}>Livr. prévue</th>
                <th className="ds-am" style={{ width: 130 }}>Montant HT</th>
                <th style={{ width: 70 }}>Lignes</th>
                <th style={{ width: 130 }}>Statut</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && bcas.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <ShoppingCart size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun bon de commande</p>
                </td></tr>
              )}
              {!isLoading && bcas.map((b) => (
                <tr key={b.id}>
                  <td><span className="ds-ref-code">{b.reference}</span></td>
                  <td>{b.fournisseur?.raisonSociale ?? '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDate(b.dateCommande)}</td>
                  <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{b.dateLivraisonPrevue ? fmtDate(b.dateLivraisonPrevue) : '—'}</td>
                  <td className="ds-am">{formatFCFA(BigInt(b.montantHtCents))}</td>
                  <td style={{ fontSize: 11 }}>{b._count?.lignes ?? b.lignes?.length ?? 0}</td>
                  <td>
                    <span className="ds-bd" style={{ background: STATUT_BCA_COLORS[b.statut], color: 'white', fontSize: 10 }}>
                      {STATUT_BCA_LABELS[b.statut]}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {b.statut === 'brouillon' && (
                        <>
                          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => handle(() => emettre.mutateAsync(b.id), `Émettre ${b.reference} ?`)} title="Émettre">
                            <Send size={11} /> Émettre
                          </button>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => remove.mutateAsync(b.id), `Supprimer ${b.reference} ?`)} style={{ color: 'var(--danger)' }} title="Supprimer">
                            <X size={11} />
                          </button>
                        </>
                      )}
                      {(b.statut === 'emis' || b.statut === 'reception_partielle') && (
                        <>
                          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setReceptionFor(b)} title="Réceptionner">
                            <PackageCheck size={11} /> Réceptionner
                          </button>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => annuler.mutateAsync(b.id), `Annuler ${b.reference} ?`)} style={{ color: 'var(--danger)' }}>
                            <X size={11} />
                          </button>
                        </>
                      )}
                      {(b.statut === 'recu_complet' || b.statut === 'annule') && (
                        <span style={{ fontSize: 10, color: 'var(--tx-3)' }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <BcaFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <ReceptionDialog bca={receptionFor} onClose={() => setReceptionFor(null)} />
    </>
  );
};

// ── Dialog création BCA ─────────────────────────────────

const BcaFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [fournisseurId, setFId] = useState('');
  const [dateCmd, setDC]        = useState(new Date().toISOString().slice(0, 10));
  const [dateLivPrev, setDLP]   = useState('');
  const [tvaPct, setTvaPct]     = useState('18');
  const [lignes, setLignes]     = useState<Array<{ matierePremiereId: string; quantite: number; prixUnitaireCents: number }>>([]);
  const [notes, setNotes]       = useState('');
  const [error, setError]       = useState<string | null>(null);

  const { data: fournData } = useFournisseurs({ actif: true, limit: 200 });
  const { data: mpData }    = useBetonMatieres({ actif: true, limit: 300 });
  const create = useCreateBca();

  useEffect(() => {
    if (open) {
      setFId(''); setDC(new Date().toISOString().slice(0, 10)); setDLP('');
      setTvaPct('18'); setLignes([]); setNotes(''); setError(null);
    }
  }, [open]);

  const addLigne = () => setLignes((prev) => [...prev, { matierePremiereId: '', quantite: 1, prixUnitaireCents: 0 }]);
  const updateLigne = (i: number, patch: Partial<typeof lignes[0]>) => setLignes((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLigne = (i: number) => setLignes((prev) => prev.filter((_, idx) => idx !== i));

  const totalHt = lignes.reduce((s, l) => s + (l.prixUnitaireCents * l.quantite), 0);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (lignes.length === 0 || lignes.some((l) => !l.matierePremiereId || l.quantite <= 0 || l.prixUnitaireCents < 0)) {
      setError('Lignes incomplètes'); return;
    }
    try {
      await create.mutateAsync({
        fournisseurId, dateCommande: dateCmd,
        dateLivraisonPrevue: dateLivPrev || undefined,
        tvaPct: parseFloat(tvaPct) || 18,
        lignes, notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Nouveau bon de commande achat" subtitle="Réf auto BCA-YYYY-NNNN · brouillon initial" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Fournisseur *">
            <select value={fournisseurId} onChange={(e) => setFId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Sélectionner —</option>
              {(fournData?.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.code} · {f.raisonSociale}</option>)}
            </select>
          </Field>
          <Field label="Date commande *">
            <input type="date" value={dateCmd} onChange={(e) => setDC(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
          </Field>
          <Field label="Livraison prévue">
            <input type="date" value={dateLivPrev} onChange={(e) => setDLP(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-blue-900">Lignes ({lignes.length})</div>
            <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={addLigne}>
              <Plus size={11} /> Ajouter
            </button>
          </div>
          {lignes.length === 0 && <div className="text-xs text-gray-500 py-2 text-center">Cliquer "Ajouter" pour saisir des lignes</div>}
          {lignes.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_120px_50px] gap-2 items-center">
              <select value={l.matierePremiereId} onChange={(e) => updateLigne(i, { matierePremiereId: e.target.value })} className="ds-fi" required>
                <option value="">— MP —</option>
                {(mpData?.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.code} · {m.nom} ({m.unite})</option>)}
              </select>
              <input type="number" min="0.01" step="0.01" value={l.quantite} onChange={(e) => updateLigne(i, { quantite: parseFloat(e.target.value) })} className="ds-fi" placeholder="Qté" />
              <input type="number" min="0" step="1" value={l.prixUnitaireCents} onChange={(e) => updateLigne(i, { prixUnitaireCents: parseInt(e.target.value, 10) || 0 })} className="ds-fi" placeholder="P.U. cents" />
              <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={() => removeLigne(i)} style={{ color: 'var(--danger)' }}>
                <X size={11} />
              </button>
            </div>
          ))}
          {totalHt > 0 && (
            <div className="text-xs font-mono text-blue-900">
              Montant HT : <strong>{formatFCFA(BigInt(totalHt))}</strong> · TVA {tvaPct}% · TTC ≈ {formatFCFA(BigInt(Math.round(totalHt * (1 + (parseFloat(tvaPct) || 0)/100))))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="TVA %">
            <input type="number" min="0" step="0.5" value={tvaPct} onChange={(e) => setTvaPct(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!fournisseurId || lignes.length === 0 || create.isPending}>
            <Plus size={14} /> Créer BCA
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog réception BCA ────────────────────────────────

const ReceptionDialog = ({ bca, onClose }: { bca: BonCommandeAchat | null; onClose: () => void }) => {
  const open = bca !== null;
  const { data: detail } = useBcaDetail(bca?.id);
  const create = useCreateReception();
  const [lignes, setLignes] = useState<Array<{ ligneBcaId: string; quantiteRecue: number; selected: boolean }>>([]);
  const [refBL, setRefBL]   = useState('');
  const [notes, setNotes]   = useState('');
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (detail?.lignes) {
      setLignes(detail.lignes.map((l) => ({
        ligneBcaId: l.id,
        quantiteRecue: parseFloat(l.quantite) - parseFloat(l.quantiteRecue),
        selected: parseFloat(l.quantite) > parseFloat(l.quantiteRecue),
      })));
      setRefBL(''); setNotes(''); setError(null);
    }
  }, [detail]);

  if (!bca || !detail) return null;

  const updateLigne = (idx: number, patch: Partial<typeof lignes[0]>) => setLignes((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const lignesPayload = lignes
      .filter((l) => l.selected && l.quantiteRecue > 0)
      .map((l) => ({ ligneBcaId: l.ligneBcaId, quantiteRecue: l.quantiteRecue }));
    if (lignesPayload.length === 0) { setError('Sélectionner au moins 1 ligne à réceptionner'); return; }
    try {
      await create.mutateAsync({
        bcaId: bca.id,
        payload: {
          refBLFournisseur: refBL.trim() || undefined,
          lignes: lignesPayload,
          notes: notes.trim() || undefined,
        },
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Réception ${bca.reference}`} subtitle={`Fournisseur ${bca.fournisseur?.raisonSociale}`} size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="N° BL fournisseur"><input value={refBL} onChange={(e) => setRefBL(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="BL-CIMAF-12345" /></Field>
          <Field label="Notes"><input value={notes} onChange={(e) => setNotes(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="text-xs font-semibold text-emerald-900 mb-2">⚠ La réception déstocke automatiquement les MP + recalcule CMP via moyenne pondérée</div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Matière</th>
                <th className="ds-am" style={{ width: 90 }}>Commandé</th>
                <th className="ds-am" style={{ width: 90 }}>Déjà reçu</th>
                <th className="ds-am" style={{ width: 110 }}>Qté à réceptionner</th>
              </tr>
            </thead>
            <tbody>
              {detail.lignes?.map((lb, i) => {
                const lr = lignes[i] ?? { selected: false, quantiteRecue: 0, ligneBcaId: lb.id };
                const reste = parseFloat(lb.quantite) - parseFloat(lb.quantiteRecue);
                return (
                  <tr key={lb.id} style={{ opacity: reste <= 0 ? 0.4 : 1 }}>
                    <td><input type="checkbox" checked={lr.selected} disabled={reste <= 0} onChange={(e) => updateLigne(i, { selected: e.target.checked })} /></td>
                    <td style={{ fontSize: 11 }}>
                      <span className="ds-ref-code">{lb.matierePremiere?.code}</span>
                      <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{lb.matierePremiere?.nom}</div>
                    </td>
                    <td className="ds-am">{formatNumber(parseFloat(lb.quantite))} {lb.matierePremiere?.unite}</td>
                    <td className="ds-am" style={{ fontSize: 11, color: 'var(--tx-3)' }}>{formatNumber(parseFloat(lb.quantiteRecue))}</td>
                    <td>
                      <input type="number" min="0" max={reste} step="0.001" value={lr.quantiteRecue}
                        onChange={(e) => updateLigne(i, { quantiteRecue: parseFloat(e.target.value) || 0 })}
                        disabled={!lr.selected || reste <= 0}
                        className="ds-fi" style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={create.isPending}>
            <PackageCheck size={14} /> {create.isPending ? 'Réception…' : 'Valider réception'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
